import { Check } from "lucide-react";
import { cn } from "@/v2/lib/utils";
import { NeedHelpCard } from "@/v2/pages/FavoritesManager/components/right-rail/NeedHelpCard";
import type { GiftStep, StepInfo } from "../types";

export function GiftStepperSidebar({
  step,
  stepsList,
  onStepClick,
  supportUrl,
}: {
  step: GiftStep;
  stepsList: StepInfo[];
  onStepClick: (id: GiftStep) => void;
  supportUrl?: string;
}) {
  return (
    <div className="w-full md:w-80 lg:w-85 shrink-0 bg-card border-b md:border-b-0 md:border-r border-border md:flex md:flex-col md:h-full md:overflow-y-auto">
      <div className="p-4 sm:p-6 lg:p-8 pb-0">
        <div>
          <h2
            className="font-bold text-lg leading-snug tracking-tight mb-4 sm:mb-8"
            style={{ color: "var(--v2-foreground)" }}
          >
            Gift Subscription
          </h2>

          <div className="space-y-10">
            {stepsList.map((st, idx) => {
              const isDone = step > st.id;
              const isCurrent = step === st.id;
              // Once the purchase is confirmed (step 4) the flow is locked: no
              // stepping back to re-run payment, which would create a duplicate
              // gift + charge.
              const isNavigable = step < 4 && (isDone || isCurrent);

              return (
                <div key={st.id} className="relative">
                  {idx < stepsList.length - 1 && (
                    <div
                      className={cn(
                        "absolute left-3.5 top-7 w-0.5 h-12 transition-colors",
                        isDone ? "bg-action" : "bg-border",
                      )}
                    />
                  )}

                  <button
                    type="button"
                    disabled={!isNavigable}
                    onClick={() => isNavigable && onStepClick(st.id)}
                    className={cn(
                      "flex items-start gap-3.5 text-left w-full group bg-transparent",
                      isNavigable ? "cursor-pointer" : "cursor-default",
                    )}
                  >
                    <div
                      className={cn(
                        "size-7 rounded-full shrink-0 flex items-center justify-center font-bold text-xs transition-all",
                        isCurrent || isDone
                          ? "bg-action text-white"
                          : "bg-card text-muted-foreground border border-border",
                        isCurrent && "ring-4 ring-action/15",
                      )}
                    >
                      {isDone ? (
                        <Check className="size-3.5 stroke-[3]" />
                      ) : (
                        st.id
                      )}
                    </div>

                    <div className="pt-0.5 min-w-0 flex-1">
                      <p
                        className={cn(
                          "text-base font-bold tracking-tight leading-tight",
                          isCurrent || isDone
                            ? "text-foreground"
                            : "text-muted-foreground",
                        )}
                      >
                        {st.title}
                      </p>
                      <p className="text-sm truncate mt-2 text-muted-foreground">
                        {isDone ? st.subCompleted : st.subActive}
                      </p>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* pt-8 as a mobile fallback: below md, the sidebar isn't a flex
          column (md:flex md:flex-col only applies at md+), so md:mt-auto
          has nothing to push against and this block sat flush against the
          last stepper item. md:pt-0 hands spacing back to the mt-auto
          bottom-of-column behavior once that layout kicks in. */}
      <div className="p-4 sm:p-6 lg:p-8 pt-8 md:pt-0 md:mt-auto">
        <div className="pt-6 sm:pt-8 border-t border-border">
          <NeedHelpCard href={supportUrl || "#"} />
        </div>
        {/* Clears the fixed mobile checkout bar for the sidebar (steps 1-2 only) */}
        {(step === 1 || step === 2) && (
          <div
            className="hidden md:block lg:hidden"
            style={{ height: 132 }}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}
